import { Suspense, lazy, useEffect, useState, useRef } from "react";
import {
	Route,
	Routes,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorPage from "./pages/Error/ErrorPage";
import ServerErrorPage from "./pages/Error/ServerErrorPage";
import Loader from "./common/Loader";
import routes from "./routes";
import ErrorBoundary from "./components/ErrorBoundary";
import ConditionalHome from "./components/ConditionalHome";
import i18n from "./i18n";

import SignIn from "./pages/Authentication/SignIn";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { userAuthAPI } from "./api/userAuthAPI";
import { getCookieValue } from "./js/utils/cookies";
import useUserStore from "./zustand/UseUserStore";
import { SESSION_ID } from "./api/apiConfig";
import { ConfettiProvider } from "./context/ConfettiContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import ConfettiRain from "./components/ConfettiRain";
import { EventTrackingInitializer } from "./components/EventTrackingInitializer";
import {
	pauseBgm,
	resumeBgm,
	isBgmPlaying,
	setBgmVolume,
	unlockAudioContext,
	getAudioContext,
	getBgmAudio,
	getBgmElement,
} from "./utils/audioManager";
// Initialize background timer singleton for persistent timers across navigation
import "./utils/backgroundTimer";

const Dashboardlayout = lazy(() => import("./layout/DashboardLayout"));

/**
 * Wraps lazy route components in Suspense. Do not defer mounting with an extra
 * `useEffect` gate — that delays the layout `<Outlet />` and can break
 * react-router context (e.g. useLocation → dispatcher.useContext errors).
 */
function LazyRouteWrapper({ Component, ...dynamicProps }) {
	return (
		<Suspense fallback={<Loader />}>
			<Component {...dynamicProps} />
		</Suspense>
	);
}

function App() {
	const [loading, setLoading] = useState(true);
	const { t } = useTranslation();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const fingerprintRef = useRef(null);
	const fingerprintInitializedRef = useRef(false);
	const state = useUserStore();
	const navigate = useNavigate();
	const bgmWasPlayingRef = useRef(false); // Track if BGM was playing before leaving BGM route
	const previousPathRef = useRef(location.pathname);
	const audioUnlockedRef = useRef(false); // Track if audio has been unlocked

	// Function to set a cookie - mobile-friendly version
	const setCookieFunction = (name, value, days) => {
		let expires = "";
		if (days) {
			const date = new Date();
			date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
			expires = `; expires=${date.toUTCString()}`;
		}

		// Mobile-friendly cookie settings
		const cookieString = `${name}=${value || ""}${expires}; path=/; SameSite=Lax`;
		document.cookie = cookieString;

		// Also store in localStorage as backup for mobile devices
		try {
			localStorage.setItem(name, value || "");
		} catch (error) {
			console.warn("Failed to set localStorage:", error);
		}
	};

	const handleGetAccount = async (userID) => {
		const response = await userAuthAPI.getaccount(userID);
		state.setUserWithRoles(response.result.member, response.result.user_roles);
		if (response.result.avatar) {
			state.buildCharacter(response.result.avatar);
		}

		// Use the reusable method from the store
		state.setupUserProgressFromAccount(response);
		// if (response.result.member.Zipcode) {
		// 	state.setAccessLevel(2);
		// } else {
		// 	state.setAccessLevel(1);
		// }
	};

	// ============================================
	// Global Audio Unlock Handler
	// ============================================
	// iOS Safari requires user gesture to unlock AudioContext
	// This handler unlocks audio on any click/touch across the entire app
	useEffect(() => {
		const unlockAudio = async () => {
			// Don't unlock if already unlocked
			if (audioUnlockedRef.current) {
				const ctx = getAudioContext();
				if (ctx && ctx.state === "running") {
					return;
				}
			}

			// Unlock AudioContext using unified audioManager
			// This now works for ANY click - buttons, modals, anywhere on the page
			try {
				const unlocked = await unlockAudioContext();
				if (unlocked) {
					audioUnlockedRef.current = true;
					console.log("✅ Audio unlocked globally via user interaction");
				}
			} catch (error) {
				console.warn("Failed to unlock audio:", error);
			}

			// CRITICAL: Always try to resume BGM if it exists but isn't playing
			// This works for both Web Audio (needs AudioContext) and HTML5 audio (needs user gesture)
			// This is especially important when coming from Kidnap page where HTML5 BGM might be paused
			// after navigation on iOS Safari
			const bgmAudio = getBgmAudio();
			const bgmElement = getBgmElement();
			if ((bgmAudio || bgmElement) && !isBgmPlaying()) {
				try {
					await resumeBgm();
					console.log("🎵 BGM resumed after user interaction");
				} catch (err) {
					// BGM might not be initialized yet - that's okay, route components will handle it
					console.log(
						"ℹ️ BGM not ready to resume yet (will be started by route component)"
					);
				}
			}
		};

		// Listen for user interactions (click, touch)
		// Use bubble phase so buttons can handle their events first
		const options = { passive: true, capture: false };

		// Add listeners to document body
		const { body } = document;
		if (body) {
			body.addEventListener("touchstart", unlockAudio, options);
			body.addEventListener("touchend", unlockAudio, options);
			body.addEventListener("click", unlockAudio, options);
		}

		// Cleanup
		return () => {
			if (body) {
				body.removeEventListener("touchstart", unlockAudio, options);
				body.removeEventListener("touchend", unlockAudio, options);
				body.removeEventListener("click", unlockAudio, options);
			}
		};
	}, []); // Run once on mount

	// Move initFingerprint outside useEffect so it can be called elsewhere
	const initFingerprint = async () => {
		// If already initialized, return immediately
		if (fingerprintInitializedRef.current) {
			return;
		}

		fingerprintInitializedRef.current = true;

		let fingerprint = null;
		try {
			const fp = await FingerprintJS.load();
			const result = await fp.get();
			fingerprint = result.visitorId;
			fingerprintRef.current = fingerprint;
		} catch (error) {
			console.error("Fingerprint generation failed:", error);
			// Fallback: generate a simple fingerprint for mobile compatibility
			fingerprint = `mobile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
			fingerprintRef.current = fingerprint;
		}

		// Make session validation request once fingerprint is available
		const localsessionid = SESSION_ID();
		console.log("localsessionid", localsessionid);
		console.log("Generated fingerprint:", fingerprint);

		try {
			const response = await userAuthAPI.sessionvalidation(
				fingerprint,
				localsessionid
			);
			const newSessionid = response.result.sessionid;
			const memberid = response.result.memberID;
			const { lastVisitedPage } = response.result;

			// Set the session ID cookie and localStorage (mobile-friendly approach)
			setCookieFunction("goodglows_sessionid", newSessionid, 3);
			localStorage.setItem("goodglows_sessionid", newSessionid);
			console.log("newSessionid", newSessionid);
			console.log("Member ID:", memberid);
			console.log("Last Visited Page:", lastVisitedPage);

			// Load storyMuted from session response (prioritize StoryMuted or AudioPreference)
			if (response.result.StoryMuted !== undefined) {
				state.setStoryMuted(response.result.StoryMuted);
				console.log(
					"Loaded storyMuted from session (StoryMuted):",
					response.result.StoryMuted
				);
			} else if (response.result.AudioPreference !== undefined) {
				// AudioPreference: "with" = unmuted (false), "without" = muted (true)
				const shouldBeMuted = response.result.AudioPreference === "without";
				state.setStoryMuted(shouldBeMuted);
				console.log(
					"Loaded storyMuted from session (AudioPreference):",
					shouldBeMuted
				);
			}

			if (memberid && memberid !== 0) {
				// Existing user - get their account
				await handleGetAccount(memberid);

				// Navigate to last visited page if available
				// Only navigate if we're on entry pages (root, sign-in, story-landing)
				// CRITICAL: Do NOT navigate away from /start if user went directly there
				// This allows the app to restore the user's previous location
				const currentPath = location.pathname;
				const isEntryPage =
					currentPath === "/" ||
					currentPath === "/signin" ||
					currentPath === "/story-landing";
				const isStartPage = currentPath === "/start";

				// Only navigate if we're on an entry page AND not on /start
				// If user went directly to /start, let them stay there
				// if (
				// 	isEntryPage &&
				// 	!isStartPage &&
				// 	lastVisitedPage &&
				// 	lastVisitedPage !== "/" &&
				// 	lastVisitedPage !== currentPath
				// ) {
				// 	console.log("📍 Navigating to last visited page:", lastVisitedPage);
				// 	startTransition(() => {
				// 		navigate(lastVisitedPage);
				// 	});
				// }
			} else {
				// For new users (memberID === 0), store the last visited page for after sign-in
				if (lastVisitedPage && lastVisitedPage !== "/") {
					localStorage.setItem("returnAfterSignIn", lastVisitedPage);
					console.log(
						"📍 Stored last visited page for new user:",
						lastVisitedPage
					);
				}
			}
		} catch (error) {
			console.error("Session validation error:", error);
			// For mobile, if session validation fails but we have an existing session,
			// try to auto-sign in with the existing session
			if (localsessionid) {
				console.log("Attempting auto-signin with existing session...");
				try {
					const autoSigninResponse =
						await userAuthAPI.autosignin(localsessionid);
					if (
						autoSigninResponse.success &&
						autoSigninResponse.result?.memberID
					) {
						console.log(
							"Auto-signin successful:",
							autoSigninResponse.result.memberID
						);
						handleGetAccount(autoSigninResponse.result.memberID);
					}
				} catch (autoSigninError) {
					console.error("Auto-signin also failed:", autoSigninError);
				}
			}
		}
		console.log("Fingerprint:", fingerprintRef.current);
	};

	useEffect(() => {
		const savedLanguage = localStorage.getItem("language");
		if (savedLanguage) {
			i18n.changeLanguage(savedLanguage);
		} else {
			i18n.changeLanguage(navigator.language.split("-")[0]);
		}

		// Check if there's already a valid session ID
		const existingSessionId =
			getCookieValue("goodglows_sessionid") ||
			localStorage.getItem("goodglows_sessionid");
		const accepted = localStorage.getItem("cookiesaccepted");

		// Check if user is on an invite URL
		const isInviteURL = location.pathname.startsWith("/accept-invite");

		// Initialize fingerprint - cookie consent is now handled in individual pages (story-landing, etc.)
		// Only auto-accept cookies for invite URLs to ensure they work smoothly
		if (isInviteURL && accepted !== "true") {
			localStorage.setItem("cookiesaccepted", "true");
		}

		// CRITICAL: Initialize fingerprint and wait for it to complete before hiding loading
		// This prevents the app from getting stuck on loading screen
		// Also set a timeout fallback to ensure loading doesn't hang forever
		const initAndSetLoading = async () => {
			try {
				await initFingerprint();
			} catch (error) {
				console.error("Fingerprint initialization error:", error);
			} finally {
				// Always set loading to false after initialization completes (or fails)
				// This ensures the app loads even if session validation fails
				setLoading(false);
			}
		};

		// Set a timeout fallback to ensure loading doesn't hang forever (max 3 seconds)
		// Reduced from 5 seconds to prevent long loading times
		const loadingTimeout = setTimeout(() => {
			console.warn("Loading timeout - forcing loading to false");
			setLoading(false);
		}, 3000);

		// Start initialization
		initAndSetLoading()
			.then(() => {
				clearTimeout(loadingTimeout);
			})
			.catch(() => {
				// If promise rejects, clear timeout and set loading to false
				clearTimeout(loadingTimeout);
				setLoading(false);
			});

		// Cleanup: clear timeout if component unmounts
		return () => {
			clearTimeout(loadingTimeout);
		};
	}, [location.pathname, navigate]);

	// Manage BGM based on route changes - pause on non-BGM pages, resume on BGM pages
	useEffect(() => {
		const currentPath = location.pathname;
		const previousPath = previousPathRef.current;

		// Define routes that should have BGM playing
		// Note: /kidnap is NOT in this list - it's video-only (no BGM)
		const bgmRoutes = [
			"/start",
			"/start-select",
			"/playersignup",
			"/choose-quest",
		];

		// Check if /starsalute should have BGM (when tutorialquest=true OR questflow=true)
		const tutorialQuest = searchParams.get("tutorialquest") === "true";
		const questFlow = searchParams.get("questflow") === "true";
		const starsaluteHasBgm =
			currentPath.startsWith("/starsalute") && (tutorialQuest || questFlow);

		// Check if /secret-salute should have BGM (when tutorialquest=true)
		const secretSaluteHasBgm =
			currentPath.startsWith("/secret-salute") && tutorialQuest;

		// Check if current route should have BGM
		const currentRouteHasBgm =
			bgmRoutes.some((route) => currentPath.startsWith(route)) ||
			starsaluteHasBgm ||
			secretSaluteHasBgm;

		// For previous route, check if it's in bgmRoutes or was /starsalute or /secret-salute
		// (We assume /starsalute and /secret-salute had BGM - this is a simplification since we can't check previous query params)
		const previousRouteHadBgm =
			bgmRoutes.some((route) => previousPath.startsWith(route)) ||
			previousPath.startsWith("/starsalute") ||
			previousPath.startsWith("/secret-salute");

		// Only act if route actually changed
		if (currentPath === previousPath) {
			return;
		}

		// If both routes have BGM, BGM should continue (don't pause or resume)
		if (previousRouteHadBgm && currentRouteHasBgm) {
			console.log("🎵 Both routes have BGM - BGM continues");
			// Update bgmWasPlayingRef to reflect current state
			bgmWasPlayingRef.current = isBgmPlaying();

			// Restore BGM volume when leaving /secret-salute (restore to quest volume 0.5)
			if (previousPath.startsWith("/secret-salute") && !secretSaluteHasBgm) {
				setTimeout(() => {
					// Use unified audioManager
					setBgmVolume(0.5);
					console.log(
						"🔊 BGM volume restored to 0.5 after leaving /secret-salute"
					);
				}, 100);
			}

			// Duck BGM volume on /secret-salute page (same as video ducking level)
			if (secretSaluteHasBgm) {
				setTimeout(() => {
					// Use unified audioManager
					setBgmVolume(0.03);
					console.log("🔇 BGM ducked to 0.03 on /secret-salute page");
				}, 100);
			}

			return;
		}

		// If leaving a BGM route, pause BGM (but preserve AudioContext)
		if (previousRouteHadBgm && !currentRouteHasBgm) {
			console.log("🎵 Leaving BGM route - pausing BGM");
			// Check if BGM was playing before leaving
			bgmWasPlayingRef.current = isBgmPlaying();

			// Restore BGM volume when leaving /secret-salute (restore to quest volume 0.5)
			if (previousPath.startsWith("/secret-salute") && !secretSaluteHasBgm) {
				setTimeout(() => {
					// Use unified audioManager
					setBgmVolume(0.5);
					console.log(
						"🔊 BGM volume restored to 0.5 after leaving /secret-salute"
					);
				}, 100);
			}

			// Pause BGM using unified audioManager
			if (isBgmPlaying()) {
				try {
					pauseBgm();
					console.log("⏸️ BGM paused");
				} catch (err) {
					console.warn("Failed to pause BGM:", err);
				}
			}
		}

		// If entering a BGM route and BGM was playing before, resume it
		if (
			!previousRouteHadBgm &&
			currentRouteHasBgm &&
			bgmWasPlayingRef.current
		) {
			console.log("🎵 Entering BGM route - resuming BGM");
			try {
				resumeBgm().catch((err) => {
					console.warn("Failed to resume BGM:", err);
				});
				console.log("▶️ BGM resumed");
			} catch (err) {
				console.warn("Error resuming BGM:", err);
			}

			// Duck BGM volume on /secret-salute page (same as video ducking level)
			if (secretSaluteHasBgm) {
				setTimeout(() => {
					// Use unified audioManager
					setBgmVolume(0.03);
					console.log("🔇 BGM ducked to 0.03 on /secret-salute page");
				}, 100);
			}
		}

		// Update previous path
		previousPathRef.current = currentPath;
	}, [location.pathname, searchParams]);

	return loading ? (
		<Loader />
	) : (
		<ConfettiProvider>
			<CookieConsentProvider>
				<EventTrackingInitializer />
				<ToastContainer
					position="bottom-right"
					autoClose={3000}
					hideProgressBar
					newestOnTop={false}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme="light"
				/>
				<ConfettiRain />
				{/* Global Mute Button - Now rendered per-component instead of globally */}
				{/* <GlobalMuteButton /> */}
				<ErrorBoundary>
					<Routes>
						<Route element={<LazyRouteWrapper Component={Dashboardlayout} />}>
							<Route path="/" element={<ConditionalHome />} />

							<Route path="/signin" element={<SignIn />} />
							{routes.map((routes, index) => {
								const { path, component: Component, props = {} } = routes;

								// You can add dynamic props here based on state or context
								const dynamicProps = {
									...props,
									// Add dynamic props from your app state
									user: state.user,
									isAuthenticated: state.userProgress.isAuthenticated,
									// Add any other dynamic props you need
								};

								const routeElement = (
									<LazyRouteWrapper Component={Component} {...dynamicProps} />
								);

								return <Route key={index} path={path} element={routeElement} />;
							})}
						</Route>
						<Route path="/error/404" element={<ServerErrorPage />} />
						<Route path="/error/500" element={<ServerErrorPage />} />
						<Route path="*" element={<ServerErrorPage />} />
					</Routes>
				</ErrorBoundary>
			</CookieConsentProvider>
		</ConfettiProvider>
	);
}

export default App;
